import styled from '@emotion/styled';
import {
  DialogTitle,
  DialogContent,
  Button,
  Dialog,
  TextField,
  DialogActions,
} from '@mui/material';
import { createSchemaSpec } from 'js/actions';
import { CREATE_SCHEMA_SPEC_REQUEST } from 'js/constants/actionTypes';
import TypographySupport from 'new/elements/TypographySupport';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { showAlert } from 'new/stores/showAlert/action';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

const styles = {
  dialogHeader: {
    padding: '16px 24px',
    color: 'rgba(0, 0, 0, 0.87)',
    fontSize: '1.25rem',
    fontWeight: 500,
  },
  dialogContentText: {
    fontSize: 16,
    color: 'rgba(0, 0, 0, 0.6)',
    padding: '0px 24px',
    marginTop: 10,
  },
};

const muiStyles = {
  dialogTitle: {
    padding: 0,
  },
  dialogContent: {
    padding: 0,
  },
};

const StyledDialogTitle = styled(DialogTitle)(muiStyles.dialogTitle);
const StyledDialogContent = styled(DialogContent)(muiStyles.dialogContent);

const SaveSchemaSpec = ({ schema, samples }) => {
  const dispatch = useDispatch<ThunkDispatch<any, any, Action>>();
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });
  const [formError, setFormError] = useState<string>(null);
  const isLoading = useIsLoading(CREATE_SCHEMA_SPEC_REQUEST);

  const onOpen = () => {
    setIsOpen(true);
  };

  const onClose = () => {
    setIsOpen(false);
  };

  const onChange = (event) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    setFormError(null);
  };

  const onSave = async () => {
    if (!formData.name) {
      setFormError('Please enter a name');
    } else {
      const payload = {
        ...formData,
        schema,
        data_samples: samples,
      };

      try {
        const createSchemaEvent = await dispatch(createSchemaSpec(payload));
        if (createSchemaEvent) {
          dispatch(
            showAlert({
              title: 'Schema Template Saved',
              severity: 'success',
            }),
          );
        }
      } catch (error) {
        dispatch(
          showAlert({
            title: 'Error Saving Schema Template',
            severity: 'error',
          }),
        );
      }
      onClose();
    }
  };

  return (
    <>
      <Button
        variant="ghost"
        color="primary"
        style={{
          fontSize: 'var(--font-size-base-sm)',
          fontStyle: 'normal',
          fontWeight: 'var(--font-weight-medium)',
          lineHeight: '24px',
          padding: '0 12px',
        }}
        onClick={onOpen}
      >
        Save Schema
      </Button>
      <Dialog
        className="save-schema-spec-dialog"
        open={isOpen}
        onClose={onClose}
        maxWidth={'sm'}
        fullWidth
      >
        <StyledDialogTitle>
          <TypographySupport style={styles.dialogHeader}>
            Save Output Schema Template
          </TypographySupport>
        </StyledDialogTitle>
        <StyledDialogContent>
          <div className="input-field-container">
            <TextField
              name="name"
              label="Name *"
              value={formData.name}
              fullWidth
              autoFocus
              error={!!formError}
              onChange={onChange}
            />
          </div>
          <div className="input-field-container">
            <TextField
              name="description"
              label="Description"
              value={formData.description}
              fullWidth
              onChange={onChange}
            />
          </div>
        </StyledDialogContent>
        <DialogActions>
          <Button variant="text" disabled={isLoading} onClick={onClose}>
            Cancel
          </Button>
          <Button variant="ghost" color="primary" disabled={isLoading} onClick={onSave}>
            {isLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SaveSchemaSpec;
